import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, EmptyRow, Spinner, TableWrap, Th } from '../../components/ui';
import { BASE, PhoneArray, keys } from './api';
import type { SrBuilding } from './api';

/** Phones building marshals have enrolled for the fire list. Platform admins can see and remove them. */
export function PhonesTab({ building, canManage }: { building: SrBuilding; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: keys.phones(building.id), queryFn: () => api.get(`${BASE}/${building.id}/phones`, { schema: PhoneArray }) });
  const remove = useMutation({
    mutationFn: (phoneId: string) => api.delete(`${BASE}/${building.id}/phones/${phoneId}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['shared-reception', 'platform'] });
      toast(t('srp.phones.removed'));
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const zone = building.timeZone;
  return (
    <>
      <ErrorLine error={remove.error} />
      <TableWrap caption={t('srp.phones.table')}>
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            <Th>{t('srp.phones.col.person')}</Th>
            <Th>{t('srp.staff.role')}</Th>
            <Th>{t('srp.phones.col.phone')}</Th>
            <Th>{t('srp.phones.col.enrolled')}</Th>
            <Th>{t('srp.phones.col.seen')}</Th>
            <Th>{t('srp.phones.col.expires')}</Th>
            {canManage ? <Th>{t('srp.col.actions')}</Th> : null}
          </tr>
        </thead>
        <tbody>
          {q.data.length === 0 ? <EmptyRow cols={canManage ? 7 : 6} /> : null}
          {q.data.map((p) => (
            <tr key={p.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-medium">{p.fullName}</td>
              <td className="px-4 py-3">{t(`sr.role.${p.role}`)}</td>
              <td className="px-4 py-3">{[p.model, p.platform, p.appVersion].filter(Boolean).join(' ')}</td>
              <td className="px-4 py-3">{formatDateTime(p.enrolledAt, zone)}</td>
              <td className="px-4 py-3">{formatDateTime(p.lastSeenAt, zone)}</td>
              <td className="px-4 py-3">{p.revokedAt ? <Badge>{t('srp.phones.removedBadge')}</Badge> : formatDateTime(p.expiresAt, zone)}</td>
              {canManage ? (
                <td className="px-4 py-3">
                  {!p.revokedAt ? (
                    <Button variant="danger" onClick={() => remove.mutate(p.id)} disabled={remove.isPending} aria-label={t('srp.phones.removeNamed', { name: p.fullName })}>
                      {t('srp.phones.remove')}
                    </Button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </>
  );
}
